import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import { 
  FaBell, 
  FaCalendarAlt, 
  FaUsers, 
  FaFilter, 
  FaCheck, 
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaSort,
  FaSearch
} from 'react-icons/fa';

const Notifications = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const notificationTypes = [
    { value: 'all', label: 'All Types', icon: FaBell },
    { value: 'appointment', label: 'Appointments', icon: FaCalendarAlt },
    { value: 'patient', label: 'Patients', icon: FaUsers },
    { value: 'system', label: 'System', icon: FaBell }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' }
  ];

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.id || !token) {
        setError('Please log in to view notifications.');
        setLoading(false);
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const [appointmentsRes, patientsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/appointments/my-appointments`, config),
          axios.get(`${process.env.REACT_APP_API_URL}/api/patients`, config),
        ]);

        const allNotifications = [];
        const today = new Date();

        // Generate appointment notifications
        if (appointmentsRes.data && Array.isArray(appointmentsRes.data)) {
          appointmentsRes.data.forEach(appt => {
            const apptDate = new Date(appt.date);
            const timeDiff = apptDate.getTime() - today.getTime();
            const hoursDiff = timeDiff / (1000 * 60 * 60);
            const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

            if (hoursDiff > 0 && hoursDiff <= 72) {
              allNotifications.push({
                id: `appt-${appt._id || appt.id}`,
                type: 'appointment',
                title: 'Upcoming Appointment',
                message: `Appointment with ${appt.patientName || 'Patient'} in ${Math.round(hoursDiff)} hours`,
                time: `${Math.round(hoursDiff)} hours`,
                date: apptDate,
                read: false,
                priority: hoursDiff <= 2 ? 'high' : hoursDiff <= 12 ? 'medium' : 'low',
                appointmentId: appt._id || appt.id,
                patientId: appt.patientId,
                status: appt.status
              });
            } else if (daysDiff > 0 && daysDiff <= 7) {
              allNotifications.push({
                id: `appt-${appt._id || appt.id}`,
                type: 'appointment',
                title: 'Upcoming Appointment',
                message: `Appointment with ${appt.patientName || 'Patient'} in ${Math.round(daysDiff)} days`,
                time: `${Math.round(daysDiff)} days`,
                date: apptDate,
                read: false,
                priority: 'low',
                appointmentId: appt._id || appt.id,
                patientId: appt.patientId,
                status: appt.status
              });
            }
          });
        }

        // Generate patient notifications
        if (patientsRes.data && Array.isArray(patientsRes.data)) {
          const recentPatients = patientsRes.data
            .filter(p => {
              const createdDate = new Date(p.createdAt || p.registrationDate || 0);
              const daysDiff = (today.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
              return daysDiff <= 7;
            })
            .slice(0, 5);

          recentPatients.forEach(patient => {
            allNotifications.push({
              id: `patient-${patient._id || patient.nationalId}`,
              type: 'patient',
              title: 'New Patient Added',
              message: `Patient ${patient.fullName} was added to your list`,
              time: 'Recently',
              date: new Date(patient.createdAt || patient.registrationDate || Date.now()),
              read: false,
              priority: 'medium',
              patientId: patient._id || patient.nationalId
            });
          });
        }

        // Add system notifications for important updates
        if (allNotifications.length === 0) {
          allNotifications.push({
            id: 'system-1',
            type: 'system',
            title: 'Welcome',
            message: 'Welcome to your notifications center. You\'ll see important updates here.',
            time: 'Today',
            date: new Date(),
            read: false,
            priority: 'low'
          });
        }

        // Sort by date (newest first)
        allNotifications.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setNotifications(allNotifications);
        setFilteredNotifications(allNotifications);

      } catch (err) {
        console.error('Fetch notifications error:', err.response?.status, err.response?.data);
        setError(err.response?.data?.message || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user, token]);

  useEffect(() => {
    let filtered = [...notifications];

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }

    // Apply status filter
    if (statusFilter === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (statusFilter === 'read') {
      filtered = filtered.filter(n => n.read);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 1;
          bValue = priorityOrder[b.priority] || 1;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        default:
          aValue = a.title;
          bValue = b.title;
      }

      if (sortOrder === 'desc') {
        return aValue > bValue ? -1 : 1;
      } else {
        return aValue < bValue ? -1 : 1;
      }
    });

    setFilteredNotifications(filtered);
  }, [notifications, typeFilter, statusFilter, searchTerm, sortBy, sortOrder]);

  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    if (notification.appointmentId) {
      navigate(`/appointments/${notification.appointmentId}`);
    } else if (notification.patientId) {
              navigate(`/dentist/patientprofile/${notification.patientId}`);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'appointment': return <FaCalendarAlt className="h-4 w-4" />;
      case 'patient': return <FaUsers className="h-4 w-4" />;
      case 'system': return <FaBell className="h-4 w-4" />;
      default: return <FaBell className="h-4 w-4" />;
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">
                    Notifications
                  </h1>
                  <p className="text-[#333333]">Stay updated with your practice activities</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={markAllAsRead}
                    className="px-4 py-2 bg-[#20B2AA] text-white rounded-lg hover:bg-[#1a9a94] transition-colors text-sm"
                  >
                    Mark All Read
                  </button>
                  <Link
                    to="/dashboard"
                    className="px-4 py-2 border border-[#0077B6] text-[#0077B6] rounded-lg hover:bg-[rgba(0,119,182,0.1)] transition-colors text-sm"
                  >
                    Back to Dashboard
                  </Link>
                </div>
              </div>

              {/* Filters and Search */}
              <div className="bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)] p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Search */}
                  <div className="lg:col-span-2">
                    <div className="relative">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      />
                    </div>
                  </div>

                  {/* Type Filter */}
                  <div>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    >
                      {notificationTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort */}
                  <div>
                    <select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => {
                        const [sort, order] = e.target.value.split('-');
                        setSortBy(sort);
                        setSortOrder(order);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    >
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="priority-desc">Priority High-Low</option>
                      <option value="priority-asc">Priority Low-High</option>
                      <option value="type-asc">Type A-Z</option>
                      <option value="type-desc">Type Z-A</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notifications List */}
              <div className="bg-white rounded-xl shadow-sm border border-[rgba(0,119,182,0.1)] overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {filteredNotifications.length} Notification{filteredNotifications.length !== 1 ? 's' : ''}
                    </h3>
                    <div className="text-sm text-gray-500">
                      {notifications.filter(n => !n.read).length} unread
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notification, index) => (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          !notification.read ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start space-x-4">
                          {/* Icon */}
                          <div className={`p-2 rounded-full ${
                            notification.type === 'appointment' ? 'bg-blue-100 text-blue-600' :
                            notification.type === 'patient' ? 'bg-green-100 text-green-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {getTypeIcon(notification.type)}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {notification.title}
                              </h4>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(notification.priority)}`}>
                                  {notification.priority}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {notification.time}
                                </span>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-2">
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center text-xs text-gray-500">
                              <span>{new Date(notification.date).toLocaleDateString()}</span>
                              {notification.appointmentId && (
                                <span className="ml-4 text-[#0077B6]">Click to view appointment</span>
                              )}
                              {notification.patientId && !notification.appointmentId && (
                                <span className="ml-4 text-[#0077B6]">Click to view patient</span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-2">
                            {!notification.read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                                title="Mark as read"
                              >
                                <FaEye className="h-4 w-4" />
                              </button>
                            )}
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete notification"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <FaBell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-lg font-medium">No notifications found</p>
                      <p className="text-sm">Try adjusting your filters or check back later</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Notifications;
